import { CommunityChannel, resolveLocalizedString } from "../models/community.model";

/**
 * Searching the communities hub: which communities and channels match a search, best first.
 * Shared by web-fender's hub and ns-myfender's Community tab so both suggest the same things in
 * the same order. Plain text only — web adds its highlight markup on top
 * (`communities-hub.search.ts`), mobile shows the text as is. Pure, so it is tested without a DOM.
 */

export const SEARCH_COMMUNITY_LIMIT = 4;
export const SEARCH_CHANNEL_LIMIT = 6;
/** Across communities, at most this many channels from any one, so a busy one doesn't fill the list. */
export const SEARCH_CHANNELS_PER_COMMUNITY = 3;
/** Description extracts: enough for a line or two around the match. */
const SNIPPET_BEFORE = 60;
const SNIPPET_LENGTH = 140;

/** The words of a search, lowercased; one-letter words match too much to mean anything. */
export function searchTokens(query: string | null | undefined): string[] {
  return Array.from(
    new Set(
      (query || "")
        .toLowerCase()
        .split(/\s+/)
        .map((t) => t.trim())
        .filter((t) => t.length > 1)
    )
  );
}

/** Admin copy can carry markup; descriptions are matched and shown as text. */
function toText(value: string): string {
  return value
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** A stretch of `text` around the first token, with "…" where it was cut. */
export function searchSnippet(text: string, tokens: string[]): string {
  const lower = text.toLowerCase();
  const at = tokens.map((t) => lower.indexOf(t)).filter((i) => i >= 0);
  const first = at.length ? Math.min(...at) : 0;
  let start = Math.max(0, first - SNIPPET_BEFORE);
  // Start on a word.
  if (start > 0) {
    const space = text.indexOf(" ", start);
    if (space > -1 && space < first) start = space + 1;
  }
  const end = Math.min(text.length, start + SNIPPET_LENGTH);
  return (start > 0 ? "…" : "") + text.substring(start, end) + (end < text.length ? "…" : "");
}

/**
 * How well a name and description match: every token has to appear in one or the other. The
 * name counts for more, and a name that starts with the search most of all. -1 = no match.
 */
function score(title: string, description: string, tokens: string[]): number {
  const t = title.toLowerCase();
  const d = description.toLowerCase();
  let total = 0;
  for (const token of tokens) {
    if (t.includes(token)) total += t.startsWith(token) || t.includes(" " + token) ? 4 : 3;
    else if (d.includes(token)) total += 1;
    else return -1;
  }
  return total;
}

/**
 * Where a match ranks: its name/description score, and how many of its posts match. Weighted
 * so a name match and a lot of matching posts count about the same — log-scaled, so a channel
 * with 200 posts doesn't bury one named after the search.
 */
function weight(nameScore: number, posts: number): number {
  return Math.max(0, nameScore) * 2 + Math.log2(1 + posts);
}

/** Posts matching the search, per community: in all, and per channel. */
export type SearchPostCounts = Record<string, { total: number; channels: Record<string, number> }>;

/**
 * One request per community counting the posts that match a search, in all and per channel —
 * a channel id only means something within one community. All go in one `search` call;
 * `hitsPerPage: 0` brings back counts, not posts.
 */
export function buildSearchCountRequests(indexName: string, query: string, communityIds: string[]): any[] {
  return (communityIds || []).filter(Boolean).map((id) => ({
    indexName,
    query,
    filters: `(community_string_id:"${String(id).replace(/"/g, '\\"')}") AND type:post`,
    hitsPerPage: 0,
    facets: ["channels"],
    maxValuesPerFacet: 100,
    relevancyStrictness: 0,
    attributesToHighlight: [],
  }));
}

/** Those requests' results, by community, in the same order as the ids they were built from. */
export function parseSearchCounts(communityIds: string[], results: any[]): SearchPostCounts {
  const out: SearchPostCounts = {};
  (communityIds || []).filter(Boolean).forEach((id, i) => {
    const result = results?.[i] || {};
    const channels: Record<string, number> = {};
    Object.entries(result.facets?.channels || {}).forEach(([channel, count]) => (channels[channel] = Number(count) || 0));
    out[id] = { total: Number(result.nbHits) || 0, channels };
  });
  return out;
}

export interface SearchCommunityInput {
  stringId: string;
  title: string;
  description: any;
  accentColor: string | null;
  thumbUrl: string | null;
  following: boolean;
}

export interface CommunitySearchResult {
  stringId: string;
  title: string;
  /** An extract of the description around the match, or "" when there is none. */
  description: string;
  accentColor: string | null;
  thumbUrl: string | null;
  /** The first letter, for a community without an image. */
  initial: string;
  following: boolean;
  /** Posts matching the search; 0 until counted, or when none do. */
  posts: number;
}

export interface ChannelSearchResult {
  communityStringId: string;
  channelStringId: string;
  title: string;
  description: string;
  communityTitle: string;
  accentColor: string | null;
  favorite: boolean;
  /** Active threads this week, to break ties. */
  threads: number;
  /** Posts in it matching the search. */
  posts: number;
}

export interface CommunitySearchResults {
  /** The search's tokens, for a caller that highlights them. */
  tokens: string[];
  communities: CommunitySearchResult[];
  channels: ChannelSearchResult[];
}

/**
 * Communities and channels matching the search, best first: by name or description, or by
 * having posts that match (`postCounts`, from Algolia — absent until it answers). With a
 * community selected only its channels (no communities); otherwise every listed community,
 * and channels across all of them — the member's own communities ahead of the rest at the
 * same weight, then the busier channel — at most three from any one community. A channel
 * already a favorite still shows, starred.
 */
export function matchCommunitySearch(
  query: string,
  communities: SearchCommunityInput[],
  channelsByCommunity: Record<string, CommunityChannel[]>,
  options: {
    lang: string;
    selectedCommunityId: string | null;
    isFavorite: (communityStringId: string, channelStringId: string) => boolean;
    threads?: (communityStringId: string, channelStringId: string) => number;
    postCounts?: SearchPostCounts | null;
    communityLimit?: number;
    channelLimit?: number;
  }
): CommunitySearchResults {
  const tokens = searchTokens(query);
  if (!tokens.length) return { tokens, communities: [], channels: [] };
  const { lang, selectedCommunityId } = options;
  const scope = selectedCommunityId ? communities.filter((c) => c.stringId === selectedCommunityId) : communities;
  const counts = options.postCounts || {};

  const communityMatches = selectedCommunityId
    ? []
    : scope
        .map((c, order) => {
          const description = toText(resolveLocalizedString(c.description, lang, "") || "");
          const posts = counts[c.stringId]?.total || 0;
          const nameScore = score(c.title, description, tokens);
          return { c, description, order, posts, matched: nameScore >= 0 || posts > 0, weight: weight(nameScore, posts) };
        })
        .filter((m) => m.matched)
        .sort((a, b) => b.weight - a.weight || Number(b.c.following) - Number(a.c.following) || a.order - b.order)
        .slice(0, options.communityLimit ?? SEARCH_COMMUNITY_LIMIT)
        .map(
          ({ c, description, posts }): CommunitySearchResult => ({
            stringId: c.stringId,
            title: c.title,
            description: description ? searchSnippet(description, tokens) : "",
            accentColor: c.accentColor,
            thumbUrl: c.thumbUrl,
            initial: (c.title || "?").trim().charAt(0).toUpperCase(),
            following: c.following,
            posts,
          })
        );

  const channelCandidates: Array<{ match: ChannelSearchResult; weight: number; following: boolean }> = [];
  for (const community of scope) {
    for (const channel of channelsByCommunity[community.stringId] || []) {
      if (channel.archived) continue;
      const title = resolveLocalizedString(channel.title, lang, channel.string_id);
      const description = toText(resolveLocalizedString(channel.description as any, lang, "") || "");
      const nameScore = score(title, description, tokens);
      const posts = counts[community.stringId]?.channels?.[channel.string_id] || 0;
      if (nameScore < 0 && !posts) continue;
      channelCandidates.push({
        weight: weight(nameScore, posts),
        following: community.following,
        match: {
          communityStringId: community.stringId,
          channelStringId: channel.string_id,
          title,
          description: description ? searchSnippet(description, tokens) : "",
          communityTitle: community.title,
          accentColor: community.accentColor,
          favorite: options.isFavorite(community.stringId, channel.string_id),
          threads: options.threads ? options.threads(community.stringId, channel.string_id) || 0 : 0,
          posts,
        },
      });
    }
  }
  const perCommunity: Record<string, number> = {};
  const channelMatches = channelCandidates
    .sort((a, b) => b.weight - a.weight || Number(b.following) - Number(a.following) || b.match.threads - a.match.threads)
    .filter((c) => {
      if (selectedCommunityId) return true;
      const id = c.match.communityStringId;
      perCommunity[id] = (perCommunity[id] || 0) + 1;
      return perCommunity[id] <= SEARCH_CHANNELS_PER_COMMUNITY;
    })
    .slice(0, options.channelLimit ?? SEARCH_CHANNEL_LIMIT)
    .map((c) => c.match);

  return { tokens, communities: communityMatches, channels: channelMatches };
}
